import { useQuery } from '@tanstack/react-query';
import type { DeployStatus } from '@pcp/shared';
import { getDeployStatus } from './api';

/**
 * Shared deploy-status poll. Both the ReleaseBanner and the ReloadPrompt read it
 * via the SAME React Query key, so React Query dedups to a single 15s poll.
 *
 * getDeployStatus() THROWS on a failed/non-OK fetch, so React Query holds the
 * last successful value across the brief API outage while services roll. That is
 * what makes "suppress the update prompt while inProgress" robust: when the API
 * container restarts mid-deploy the poll fails, React Query keeps the last
 * `inProgress:true`, and we keep suppressing — we never flash the prompt in the
 * unstable window just because a single poll failed.
 */
export function useDeployStatus(): { inProgress: boolean; status: DeployStatus | undefined } {
  const { data } = useQuery({
    queryKey: ['deploy-status'],
    queryFn: getDeployStatus,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
  return { inProgress: data?.inProgress ?? false, status: data };
}
